import React, { Fragment, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Menu, Transition } from '@headlessui/react';
import {
  EllipsisVerticalIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
  RocketLaunchIcon,
} from '@heroicons/react/20/solid';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const GripIcon = ({ className }) => (
  <svg
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <circle cx="7.5" cy="5" r="1.25" />
    <circle cx="7.5" cy="10" r="1.25" />
    <circle cx="7.5" cy="15" r="1.25" />
    <circle cx="12.5" cy="5" r="1.25" />
    <circle cx="12.5" cy="10" r="1.25" />
    <circle cx="12.5" cy="15" r="1.25" />
  </svg>
);

function MenuCard({ id, menu, selected, setSelected, onClickEditMenu, onClickDuplicateMenu, onClickPublishMenu }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: menu.id });

  const menuBtnRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const updateDropdownPosition = () => {
    if (!menuBtnRef.current) return;
    const rect = menuBtnRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setDropdownStyle({
      position: 'fixed',
      zIndex: 9999,
      ...(spaceBelow < 60
        ? {
            bottom: window.innerHeight - rect.top + 4,
            right: window.innerWidth - rect.right,
          }
        : { top: rect.bottom + 4, right: window.innerWidth - rect.right }),
    });
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      onClick={() => {
        setSelected((prev) => ({
          ...prev,
          menuid: menu.id,
          category: {},
          product: {},
          productid: '',
          itemid: '',
          optiongroupid: '',
          optionid: '',
          isCategoryFetch: !prev.isCategoryFetch,
        }));
      }}
      onDoubleClick={() => onClickEditMenu(menu)}
      key={id}
      className={classNames(
        menu.id === selected.menuid
          ? 'border-blue-400 bg-blue-50/80 text-slate-900 shadow-sm ring-1 ring-blue-200 dark:border-sky-400 dark:bg-slate-700 dark:text-slate-100 dark:ring-sky-500/30'
          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700/70 dark:text-slate-300 dark:hover:bg-slate-600',
        isDragging ? 'shadow-lg' : '',
        'group relative flex min-h-[4.85rem] max-w-[13rem] min-w-[13rem] cursor-pointer items-center justify-between rounded-md border pl-1 pr-3 py-2.5 font-medium text-sm transition-all duration-150',
      )}
    >
      <button
        type="button"
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        aria-label="Drag to reorder"
        className={classNames(
          isDragging
            ? 'cursor-grabbing text-slate-500 dark:text-slate-300'
            : 'cursor-grab text-slate-300 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-300',
          'flex h-full shrink-0 items-center justify-center rounded-md px-1 opacity-40 transition group-hover:opacity-100 focus:opacity-100 focus:outline-none',
        )}
      >
        <GripIcon className="h-4 w-4" />
      </button>
      <div
        className="min-w-0 flex-1 pl-1 pr-2"
        aria-current={selected.menuid ? 'page' : undefined}
      >
        <div className="flex min-w-0 items-center gap-1 overflow-hidden font-semibold">
          <span className="min-w-0 truncate">{menu.name}</span>
          {(!menu.menustores || menu.menustores.length === 0) && (
            <span className="inline-flex shrink-0 items-center rounded-full border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-amber-700 dark:border-amber-500/50 dark:bg-amber-900/30 dark:text-amber-200">
              Draft
            </span>
          )}
          {menu.origintype === 10 && (
            <span className="inline-flex shrink-0 items-center rounded-full border border-sky-300 bg-sky-100 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-sky-700 dark:border-sky-500/50 dark:bg-sky-900/30 dark:text-sky-200">
              Square
            </span>
          )}
        </div>
        <p
          className={classNames(
            menu.id === selected.menuid
              ? 'text-slate-500 dark:text-slate-300'
              : 'text-slate-400',
            'min-h-[1rem] text-xs font-light truncate',
          )}
        >
          {menu.description || 'No description'}
        </p>
      </div>
      <div
        className="flex items-center flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <Menu as="div" className="relative">
          <Menu.Button
            ref={menuBtnRef}
            onClick={updateDropdownPosition}
            className="inline-flex items-center rounded-md p-1 text-slate-300 transition hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200"
          >
            <EllipsisVerticalIcon className="h-5 w-5" />
          </Menu.Button>
          {createPortal(
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                style={dropdownStyle}
                className="w-36 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-slate-700 dark:ring-slate-600"
              >
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={classNames(
                        active ? 'bg-slate-50 dark:bg-slate-600' : '',
                        'flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200',
                      )}
                      onClick={() => onClickEditMenu(menu)}
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                      Edit
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={classNames(
                        active ? 'bg-slate-50 dark:bg-slate-600' : '',
                        'flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200',
                      )}
                      onClick={() => onClickDuplicateMenu(menu)}
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                      Duplicate
                    </button>
                  )}
                </Menu.Item>
                {(!menu.menustores || menu.menustores.length === 0) && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={classNames(
                          active ? 'bg-slate-50 dark:bg-slate-600' : '',
                          'flex w-full items-center gap-2 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300',
                        )}
                        onClick={() => onClickPublishMenu(menu)}
                      >
                        <RocketLaunchIcon className="h-4 w-4" />
                        Go Live
                      </button>
                    )}
                  </Menu.Item>
                )}
              </Menu.Items>
            </Transition>,
            document.body,
          )}
        </Menu>
      </div>
    </div>
  );
}

export default MenuCard;
